'use client';

// Copyright 2023 Vercel, Inc. Licensed under the Apache License, Version 2.0.
// https://www.apache.org/licenses/LICENSE-2.0
// Adapted from Vercel AI Elements:
// https://github.com/vercel/ai-elements/blob/main/packages/elements/src/reasoning.tsx
// Uses Cortex's existing Streamdown and reduced-motion-aware progress styling.

import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { Collapsible, CollapsibleTrigger } from '@/shared/ui/shadcn/collapsible';
import { cn } from '@/lib/utils';
import { ChevronRightIcon } from 'lucide-react';
import type { ComponentProps, HTMLAttributes, ReactNode } from 'react';
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import { Streamdown } from 'streamdown';
import { useStickToBottom } from 'use-stick-to-bottom';

interface ReasoningContextValue {
  isStreaming: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  duration: number | undefined;
  contentId: string;
}

const ReasoningContext = createContext<ReasoningContextValue | null>(null);

const useReasoning = () => {
  const context = useContext(ReasoningContext);
  if (!context) {
    throw new Error('Reasoning components must be used within Reasoning');
  }
  return context;
};

export type ReasoningProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
};

const MS_IN_S = 1000;

export const Reasoning = memo(
  ({
    className,
    isStreaming = false,
    open,
    defaultOpen,
    onOpenChange,
    duration: durationProp,
    children,
    ...props
  }: ReasoningProps) => {
    const resolvedDefaultOpen = defaultOpen ?? isStreaming;
    // Track if defaultOpen was explicitly set to false (to prevent auto-open)
    const isExplicitlyClosed = defaultOpen === false;

    const [isOpen, setIsOpen] = useControllableState<boolean>({
      defaultProp: resolvedDefaultOpen,
      onChange: onOpenChange,
      prop: open,
    });
    const [duration, setDuration] = useControllableState<number | undefined>({
      defaultProp: undefined,
      prop: durationProp,
    });

    const hasEverStreamedRef = useRef(isStreaming);
    const hasAutoClosedRef = useRef(false);
    const manuallyToggledRef = useRef(false);
    const startTimeRef = useRef<number | null>(null);

    // Track when streaming starts and compute duration
    useEffect(() => {
      if (isStreaming) {
        hasEverStreamedRef.current = true;
        hasAutoClosedRef.current = false;
        if (startTimeRef.current === null) {
          startTimeRef.current = Date.now();
        }
      } else if (startTimeRef.current !== null) {
        setDuration(Math.ceil((Date.now() - startTimeRef.current) / MS_IN_S));
        startTimeRef.current = null;
      }
    }, [isStreaming, setDuration]);

    // Auto-open when streaming starts (unless explicitly closed)
    useEffect(() => {
      if (isStreaming && !isOpen && !isExplicitlyClosed && !manuallyToggledRef.current) {
        setIsOpen(true);
      }
    }, [isStreaming, isOpen, setIsOpen, isExplicitlyClosed]);

    // Fold away as soon as the answer starts (once only, and only if it ever streamed),
    // so the answer begins right under the trigger instead of jumping up a moment later.
    useEffect(() => {
      if (
        hasEverStreamedRef.current &&
        !isStreaming &&
        isOpen &&
        !hasAutoClosedRef.current &&
        !manuallyToggledRef.current
      ) {
        hasAutoClosedRef.current = true;
        setIsOpen(false);
      }
    }, [isStreaming, isOpen, setIsOpen]);

    const handleOpenChange = useCallback(
      (newOpen: boolean) => {
        // Keep the physician's explicit choice for this response.
        manuallyToggledRef.current = true;
        setIsOpen(newOpen);
      },
      [setIsOpen]
    );

    const contentId = useId();
    const contextValue = useMemo(
      () => ({ contentId, duration, isOpen, isStreaming, setIsOpen }),
      [contentId, duration, isOpen, isStreaming, setIsOpen]
    );

    return (
      <ReasoningContext.Provider value={contextValue}>
        <Collapsible
          className={cn('not-prose w-full min-w-0', className)}
          onOpenChange={handleOpenChange}
          open={isOpen}
          {...props}
        >
          {children}
        </Collapsible>
      </ReasoningContext.Provider>
    );
  }
);

export type ReasoningTriggerProps = ComponentProps<typeof CollapsibleTrigger> & {
  getThinkingMessage?: (isStreaming: boolean, duration?: number) => ReactNode;
};

const defaultGetThinkingMessage = (isStreaming: boolean, duration?: number) => {
  if (isStreaming) {
    return <span className="motion-safe:animate-status-pulse">Thinking…</span>;
  }
  if (duration === undefined) {
    return <span>View reasoning</span>;
  }
  return <span>Thought for {duration}s</span>;
};

export const ReasoningTrigger = memo(
  ({
    className,
    children,
    getThinkingMessage = defaultGetThinkingMessage,
    ...props
  }: ReasoningTriggerProps) => {
    const { contentId, isStreaming, isOpen, duration } = useReasoning();

    return (
      <CollapsibleTrigger
        aria-controls={contentId}
        className={cn(
          'flex w-fit max-w-full items-center gap-1 rounded-md py-1 text-left text-sm font-normal text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className
        )}
        {...props}
      >
        <>
          {children ?? getThinkingMessage(isStreaming, duration)}
          <ChevronRightIcon
            aria-hidden="true"
            className={cn(
              'size-3.5 shrink-0 transition-transform motion-reduce:transition-none',
              isOpen && 'rotate-90'
            )}
          />
        </>
      </CollapsibleTrigger>
    );
  }
);

export type ReasoningContentProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
  children: string;
};

/**
 * Stays mounted while collapsed so the markdown is already laid out when a physician opens it.
 * The grid-rows transition animates its natural height, so nothing has to be measured first.
 */
export const ReasoningContent = memo(({ className, children, ...props }: ReasoningContentProps) => {
  const { contentId, isOpen, isStreaming } = useReasoning();
  // Live reasoning follows the latest thought until the physician scrolls; a reopened trace reads from the top.
  const { scrollRef, contentRef, stopScroll } = useStickToBottom({
    initial: isStreaming ? 'instant' : false,
    resize: 'smooth',
  });

  useEffect(() => {
    if (isOpen || isStreaming) {
      return;
    }
    stopScroll();
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isOpen, isStreaming, scrollRef, stopScroll]);

  return (
    <div
      id={contentId}
      aria-hidden={!isOpen}
      data-state={isOpen ? 'open' : 'closed'}
      inert={!isOpen}
      className={cn(
        'grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none',
        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        className
      )}
      {...props}
    >
      <div className="min-h-0 overflow-hidden">
        {/* The bottom margin gives the answer its own block once the trace is open. */}
        <div
          ref={scrollRef}
          className="scroll-edge-fade mt-1 mb-2 max-h-28 overflow-y-auto overscroll-contain"
        >
          <div
            ref={contentRef}
            className="text-[13px] leading-relaxed font-normal text-muted-foreground [overflow-wrap:anywhere] [&_:is(h1,h2,h3,h4,h5,h6)]:text-[13px] [&_:is(h1,h2,h3,h4,h5,h6,strong,b)]:font-medium [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
          >
            <Streamdown controls={{ table: false }}>{children}</Streamdown>
          </div>
        </div>
      </div>
    </div>
  );
});

Reasoning.displayName = 'Reasoning';
ReasoningTrigger.displayName = 'ReasoningTrigger';
ReasoningContent.displayName = 'ReasoningContent';
